import React, { useState } from 'react';
import { X, Mail, Key, Lock } from 'lucide-react';
import { API_URL } from '../../config';

export default function ForgotPasswordModal({ onClose }) {
  const [step, setStep] = useState(1); // 1=Email, 2=Code+Password
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleRequestCode = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const res = await fetch(`${API_URL}/api/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });

      const data = await res.json();

      if (res.ok) {
        setStep(2);
      } else {
        setError(data.detail || 'Fehler beim Versenden');
      }
    } catch (err) {
      setError('Netzwerkfehler');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    
    if (newPassword.length < 8) {
      setError('Passwort muss mindestens 8 Zeichen lang sein');
      return;
    }
    
    setLoading(true);
    setError('');

    try {
      const res = await fetch(`${API_URL}/api/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, code, new_password: newPassword })
      });

      const data = await res.json();

      if (res.ok) {
        alert('✅ Passwort erfolgreich geändert! Du kannst dich jetzt anmelden.');
        onClose();
      } else {
        setError(data.detail || 'Ungültiger Code');
      }
    } catch (err) {
      setError('Netzwerkfehler');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/90 backdrop-blur-sm z-[100] flex items-center justify-center p-4">
      <div className="bg-gradient-to-br from-black/95 to-black/90 border-2 border-[#10B981]/30 rounded-2xl p-8 max-w-md w-full relative">
        
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 w-10 h-10 flex items-center justify-center rounded-full border-2 border-white/20 text-white hover:bg-white/10 transition-all"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Header */}
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-white mb-2">Passwort zurücksetzen</h2>
          <p className="text-white/60 text-sm">
            {step === 1 ? 'Gib deine E-Mail ein, um einen Code zu erhalten' : 'Gib den Code und dein neues Passwort ein'}
          </p>
        </div>

        {/* Step Indicator */}
        <div className="flex items-center gap-2 mb-6">
          <div className={`flex-1 h-2 rounded-full ${step >= 1 ? 'bg-[#10B981]' : 'bg-white/10'}`}></div>
          <div className={`flex-1 h-2 rounded-full ${step >= 2 ? 'bg-[#10B981]' : 'bg-white/10'}`}></div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-500 text-sm">
            {error}
          </div>
        )}

        {/* Step 1: Email */}
        {step === 1 && (
          <form onSubmit={handleRequestCode} className="space-y-4">
            <div>
              <label className="block text-white/80 text-sm mb-2">E-Mail</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#10B981]/50" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="w-full pl-12 pr-4 py-3 bg-black/50 border-2 border-[#10B981]/30 rounded-lg text-white focus:border-[#10B981] focus:ring-4 focus:ring-[#10B981]/20 transition-all"
                  placeholder="deine@email.de"
                  data-testid="forgot-password-email"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 bg-gradient-to-r from-[#10B981] to-[#059669] text-black rounded-lg font-bold hover:shadow-xl hover:shadow-[#10B981]/50 transition-all disabled:opacity-50"
              data-testid="request-code-btn"
            >
              {loading ? 'Sende...' : 'Code anfordern'}
            </button>
          </form>
        )}

        {/* Step 2: Code + New Password */}
        {step === 2 && (
          <form onSubmit={handleResetPassword} className="space-y-4">
            <div className="p-3 bg-[#10B981]/10 border border-[#10B981]/30 rounded-lg text-[#10B981] text-sm mb-4">
              ✅ Code wurde an <strong>{email}</strong> gesendet (gültig 10 Min)
            </div>

            <div>
              <label className="block text-white/80 text-sm mb-2">Code aus E-Mail (nicht 2FA-Code)</label>
              <div className="relative">
                <Key className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#10B981]/50" />
                <input
                  type="text"
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  required
                  maxLength={6}
                  className="w-full pl-12 pr-4 py-3 bg-black/50 border-2 border-[#10B981]/30 rounded-lg text-white text-center text-2xl tracking-widest focus:border-[#10B981] focus:ring-4 focus:ring-[#10B981]/20 transition-all"
                  placeholder="E-Mail-Code"
                  data-testid="reset-code-input"
                />
              </div>
              <p className="text-white/40 text-xs mt-1">📧 6-stelliger Code aus der E-Mail</p>
            </div>

            <div>
              <label className="block text-white/80 text-sm mb-2">Neues Passwort</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#10B981]/50" />
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  minLength={8}
                  className="w-full pl-12 pr-4 py-3 bg-black/50 border-2 border-[#10B981]/30 rounded-lg text-white focus:border-[#10B981] focus:ring-4 focus:ring-[#10B981]/20 transition-all"
                  placeholder="Mindestens 8 Zeichen"
                  data-testid="new-password-input"
                />
              </div>
              <p className="text-white/40 text-xs mt-1">Mindestens 8 Zeichen</p>
            </div>

            <button
              type="submit"
              disabled={loading || code.length !== 6}
              className="w-full py-3 bg-gradient-to-r from-[#10B981] to-[#059669] text-black rounded-lg font-bold hover:shadow-xl hover:shadow-[#10B981]/50 transition-all disabled:opacity-50"
              data-testid="reset-password-btn"
            >
              {loading ? 'Speichere...' : 'Passwort ändern'}
            </button>

            <button
              type="button"
              onClick={() => setStep(1)}
              className="w-full text-white/60 hover:text-white text-sm"
            >
              ← Zurück
            </button>
          </form>
        )}

      </div>
    </div>
  );
}
